import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, FileText, Printer, RefreshCw, Search, X } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { supabase } from '../../../lib/supabase';
import { systemService } from '../../../services/systemService';
import { studentService } from '../../../services/studentService';

const amount = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatMoney = value => amount(value).toLocaleString('en-KE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const dateLabel = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('en-KE', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(date);
};

const dateTimeLabel = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('en-KE', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const methodFromDescription = description => {
  const match = String(description || '').match(/payment\s+via\s+(.+)/i);
  return match?.[1]?.trim()?.toLowerCase() || '—';
};

const receiptKey = receipt => String(receipt.receipt_number || receipt.doc_no || receipt.id || 'receipt');

const PaymentReceipts = ({ student }) => {
  const [institution, setInstitution] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [statementBalance, setStatementBalance] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let current = true;
    const load = async () => {
      const studentId = student?.profile_id || student?.id;
      if (!studentId) {
        if (current) setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const [institutionResult, statementResult, paymentResult] = await Promise.all([
          systemService.getInstitutionDetails(),
          studentService.getStudentStatement(studentId),
          supabase
            .from('student_payments')
            .select('id,amount,payment_method,receipt_number,payment_date,status,reconciliation_status,reconciliation_reference,notes')
            .eq('student_id', studentId)
            .order('payment_date', { ascending: false }),
        ]);

        if (!current) return;
        setInstitution(institutionResult || null);

        const statementRows = Array.isArray(statementResult?.data) ? statementResult.data : [];
        const lastStatementRow = statementRows[statementRows.length - 1];
        setStatementBalance(amount(lastStatementRow?.running_balance));

        // The payment table contains receipt metadata. The statement ledger is
        // the fallback for older deployments or roles that cannot read payment
        // rows directly, and also guarantees every posted credit is visible.
        const paymentRows = !paymentResult.error && Array.isArray(paymentResult.data) ? paymentResult.data : [];
        const paymentReceipts = paymentRows
          .filter(payment => String(payment.status || 'paid').toLowerCase() === 'paid')
          .map(payment => ({
            ...payment,
            amount: amount(payment.amount),
            receipt_number: payment.receipt_number || `REC-${String(payment.id).slice(0, 8).toUpperCase()}`,
            payment_method: payment.payment_method || '—',
            source: 'payment',
          }));
        const ledgerReceipts = statementRows
          .filter(row => String(row.transaction_type || '').toLowerCase() === 'payment')
          .map(row => ({
            id: row.id,
            amount: amount(row.credit_amount),
            receipt_number: row.doc_no || `REC-${String(row.id).slice(0, 8).toUpperCase()}`,
            payment_date: row.transaction_date,
            payment_method: methodFromDescription(row.description),
            status: 'paid',
            reconciliation_status: 'recorded',
            description: row.description,
            source: 'ledger',
          }));

        const knownReferences = new Set(paymentReceipts.flatMap(receipt => [receipt.receipt_number, receipt.id].filter(Boolean).map(String)));
        const merged = [...paymentReceipts, ...ledgerReceipts.filter(receipt => !knownReferences.has(String(receipt.receipt_number)))]
          .sort((first, second) => new Date(second.payment_date || 0).getTime() - new Date(first.payment_date || 0).getTime());
        setReceipts(merged);
        if (!merged.length && paymentResult.error && !statementRows.length) {
          setError('Payment receipts are not available yet.');
        }
      } catch (loadError) {
        if (!current) return;
        console.error('Unable to load payment receipts:', loadError);
        setError(loadError?.message || 'Payment receipts could not be loaded.');
      } finally {
        if (current) setLoading(false);
      }
    };

    load();
    return () => { current = false; };
  }, [student?.profile_id, student?.id]);

  const visibleReceipts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return receipts;
    return receipts.filter(receipt => [receipt.receipt_number, receipt.payment_method, receipt.reconciliation_status, receipt.notes]
      .some(value => String(value || '').toLowerCase().includes(query)));
  }, [receipts, search]);

  const totalPaid = receipts.reduce((total, receipt) => total + amount(receipt.amount), 0);
  const lastPayment = receipts[0]?.payment_date;
  const studentName = [student?.first_name, student?.middle_name, student?.last_name].filter(Boolean).join(' ') || 'Student';

  const downloadReceipt = async receipt => {
    setSelectedReceipt(receipt);
    await new Promise(resolve => setTimeout(resolve, 80));
    const element = document.getElementById('student-payment-receipt');
    if (!element) return;
    try {
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const image = canvas.toDataURL('image/jpeg', 0.96);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 12;
      const usableWidth = 210 - margin * 2;
      const usableHeight = 297 - margin * 2;
      const imageHeight = (canvas.height * usableWidth) / canvas.width;
      const scale = Math.min(1, usableHeight / imageHeight);
      const width = usableWidth * scale;
      const height = imageHeight * scale;
      pdf.addImage(image, 'JPEG', (210 - width) / 2, margin, width, height);
      pdf.save(`${receipt.receipt_number || 'payment-receipt'}.pdf`);
    } catch (downloadError) {
      console.error('Failed to generate payment receipt PDF:', downloadError);
      setError(downloadError?.message || 'The receipt PDF could not be generated.');
    }
  };

  if (loading) {
    return <div className="grid min-h-64 place-items-center rounded-xl border border-slate-200 bg-white"><RefreshCw className="h-7 w-7 animate-spin text-primary" /></div>;
  }

  if (error) {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">{error}</div>;
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Finance</p>
          <h1 className="text-2xl font-bold text-slate-950">Payment receipts</h1>
          <p className="mt-1 text-sm text-slate-500">View and download receipts for payments posted to your account.</p>
        </div>
        <label className="relative w-full sm:w-72"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><span className="sr-only">Search receipts</span><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search receipt or payment method" className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3 print:hidden">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium text-slate-500">Receipts</p><p className="mt-2 text-2xl font-bold text-slate-950">{receipts.length}</p><p className="mt-1 text-xs text-slate-500">Posted payment records</p></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium text-slate-500">Total paid</p><p className="mt-2 text-2xl font-bold text-emerald-700">KES {formatMoney(totalPaid)}</p><p className="mt-1 text-xs text-slate-500">Across all receipts</p></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium text-slate-500">Last payment</p><p className="mt-2 text-lg font-bold text-slate-950">{dateLabel(lastPayment)}</p><p className="mt-1 text-xs text-slate-500">Statement balance: {statementBalance < 0 ? `Credit KES ${formatMoney(Math.abs(statementBalance))}` : `KES ${formatMoney(statementBalance)}`}</p></div>
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm print:hidden">
        <div className="border-b border-slate-200 px-5 py-4"><h2 className="text-base font-bold text-slate-950">Receipt history</h2><p className="mt-1 text-xs text-slate-500">Only successful payments posted to the student ledger appear here.</p></div>
        {visibleReceipts.length ? <div className="divide-y divide-slate-200">{visibleReceipts.map(receipt => {
          const reconciliation = String(receipt.reconciliation_status || 'recorded').toLowerCase();
          const reconciliationLabel = reconciliation === 'reconciled' ? 'Reconciled' : reconciliation === 'exception' ? 'Needs review' : 'Recorded';
          return <div key={receiptKey(receipt)} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 hover:bg-slate-50/60"><div className="flex min-w-0 items-center gap-3"><div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-5 w-5" /></div><div className="min-w-0"><p className="truncate font-mono text-sm font-bold text-slate-900">{receipt.receipt_number}</p><p className="mt-0.5 text-xs capitalize text-slate-500">{dateTimeLabel(receipt.payment_date)} · {receipt.payment_method || 'Payment'}</p></div></div><div className="flex items-center gap-4"><div className="text-right"><p className="font-bold text-emerald-700">KES {formatMoney(receipt.amount)}</p><span className={`text-[11px] font-semibold ${reconciliation === 'exception' ? 'text-amber-700' : 'text-slate-500'}`}>{reconciliationLabel}</span></div><button type="button" onClick={() => setSelectedReceipt(receipt)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><FileText className="h-3.5 w-3.5" />View</button><button type="button" onClick={() => downloadReceipt(receipt)} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"><Download className="h-3.5 w-3.5" />PDF</button></div></div>;
        })}</div> : <div className="grid place-items-center px-5 py-16 text-center"><div className="grid h-14 w-14 place-items-center rounded-full bg-slate-100 text-slate-400"><FileText className="h-7 w-7" /></div><p className="mt-4 font-semibold text-slate-800">{search ? 'No receipts match your search' : 'No payment receipts yet'}</p><p className="mt-1 max-w-sm text-sm text-slate-500">{search ? 'Try another receipt number or payment method.' : 'Receipts will appear here after a payment is successfully posted to your account.'}</p></div>}
      </section>

      {selectedReceipt && <div className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-label="Payment receipt"><div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"><div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 print:hidden"><div><h2 className="text-base font-bold text-slate-950">Payment receipt</h2><p className="text-xs text-slate-500">{selectedReceipt.receipt_number}</p></div><button type="button" onClick={() => setSelectedReceipt(null)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close receipt"><X className="h-5 w-5" /></button></div><div id="student-payment-receipt" className="p-6 sm:p-10"><div className="flex items-start justify-between gap-6 border-b-2 border-primary pb-6"><div className="flex items-center gap-3">{institution?.logo_url ? <img src={institution.logo_url} alt="Institution logo" className="h-14 w-14 rounded-lg border border-slate-200 object-contain p-1" /> : <div className="grid h-14 w-14 place-items-center rounded-lg bg-primary/10 text-primary"><FileText className="h-7 w-7" /></div>}<div><p className="font-bold uppercase tracking-wide text-slate-950">{institution?.name || 'Institution'}</p><p className="mt-1 text-xs text-slate-500">{institution?.postal_address || institution?.address || institution?.email || ''}</p></div></div><div className="text-right"><p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">Official receipt</p><p className="mt-2 font-mono text-sm font-bold text-slate-950">{selectedReceipt.receipt_number}</p><p className="mt-1 text-xs text-slate-500">{dateLabel(selectedReceipt.payment_date)}</p></div></div><div className="mt-6 grid gap-4 sm:grid-cols-2"><div className="rounded-lg bg-slate-50 p-4"><p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Received from</p><p className="mt-2 font-bold text-slate-950">{studentName}</p><p className="mt-1 font-mono text-xs text-slate-600">{student?.admission_number || student?.registration_number || 'Admission number not set'}</p></div><div className="rounded-lg bg-slate-50 p-4"><p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Payment details</p><p className="mt-2 text-sm capitalize text-slate-700">Method: <strong>{selectedReceipt.payment_method || '—'}</strong></p><p className="mt-1 text-xs text-slate-600">Posted: {dateTimeLabel(selectedReceipt.payment_date)}</p><p className="mt-1 text-xs text-slate-600">Reference: {selectedReceipt.reconciliation_reference || selectedReceipt.receipt_number || '—'}</p></div></div><div className="mt-7 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center"><p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-800">Amount received</p><p className="mt-2 text-3xl font-extrabold text-emerald-900">KES {formatMoney(selectedReceipt.amount)}</p><p className="mt-2 text-xs text-emerald-800">Payment posted to the student ledger</p></div>{selectedReceipt.notes && <div className="mt-5 rounded-lg border border-slate-200 p-4 text-sm text-slate-600"><span className="font-semibold text-slate-800">Notes:</span> {selectedReceipt.notes}</div>}<div className="mt-7 border-t border-slate-200 pt-4 text-xs leading-relaxed text-slate-500"><p>This receipt confirms that the amount above was recorded against the student account. It is not transferable and should be retained for official reference.</p><p className="mt-3">Generated on {dateTimeLabel(new Date())}</p></div></div><div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4 print:hidden"><button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Printer className="h-4 w-4" />Print</button><button type="button" onClick={() => downloadReceipt(selectedReceipt)} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"><Download className="h-4 w-4" />Download PDF</button></div></div></div>}

      <style jsx="true">{`
        @media print {
          body * { visibility: hidden; }
          #root, #root * { visibility: visible; }
          header, nav, aside, footer { display: none !important; }
          .print\\:hidden { display: none !important; }
          #student-payment-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
          }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
};

export default PaymentReceipts;
